import { useEffect, useRef, useState } from 'react';
import { Box, CardMedia, IconButton, Skeleton, type SxProps, type Theme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import type { ImagemProduto } from '../api';
import { urlImagemProduto, type OpcoesImagem } from '../lib/imagem';

/** Distância mínima (px) para um arrasto contar como troca de imagem. */
const LIMIAR_SWIPE = 40;

interface GaleriaProdutoProps {
  imagens: ImagemProduto[];
  alt: string;
  /** Dimensões pedidas ao ImageBoss — iguais às que a tela já usava. */
  opcoes: OpcoesImagem;
  /** Estilo da `<img>` (tamanho, objectFit, borda) — vem da tela chamadora. */
  sx?: SxProps<Theme>;
  /** Distância (px) das bolinhas até a base — sobe quando algo cobre o pé da imagem. */
  recuoIndicadores?: number;
  /** Arredondamento da imagem (unidades do tema) — fica na moldura para valer também no carregamento. */
  arredondamento?: number;
}

// Mesmo tamanho do card da lista: quase sempre já está no cache quando o produto abre
const OPCOES_MINIATURA: OpcoesImagem = { largura: 240, altura: 240, modo: 'cover' };

/**
 * Imagem do produto com rotação quando há mais de uma.
 *
 * Com uma única imagem: sem setas, sem indicadores e sem handler de arrasto.
 * Enquanto a foto grande carrega, mostra skeleton + miniatura desfocada.
 *
 * Com duas ou mais: bolinhas sempre, arrasto no touch e setas só onde há mouse
 * (desktop) — no celular e no tablet a troca é deslizando. As demais imagens são
 * pré-carregadas escondidas, então a troca é instantânea e sem piscada — o que
 * dispensa animação de transição.
 */
export function GaleriaProduto({ imagens, alt, opcoes, sx, recuoIndicadores = 8, arredondamento = 0 }: GaleriaProdutoProps): JSX.Element | null {
  const [indice, setIndice] = useState(0);
  const [carregadas, setCarregadas] = useState<ReadonlySet<string>>(() => new Set());
  const inicioToque = useRef<number | null>(null);

  // Produto trocado (ou imagem removida no cadastro): volta para a primeira.
  useEffect(() => {
    setIndice(0);
  }, [imagens]);

  if (imagens.length === 0) return null;

  const atual = imagens[Math.min(indice, imagens.length - 1)]!;
  const varias = imagens.length > 1;

  function irPara(proximo: number): void {
    setIndice((proximo + imagens.length) % imagens.length);
  }

  const urlAtual = urlImagemProduto(atual, opcoes) ?? '';
  const carregada = carregadas.has(urlAtual);

  // Enquanto a foto grande chega: skeleton ao fundo e a miniatura desfocada por cima
  const imagem = (
    <Box sx={{ position: 'relative', overflow: 'hidden', borderRadius: arredondamento }}>
      {!carregada && (
        <>
          <Skeleton variant="rectangular" animation="wave" sx={{ position: 'absolute', inset: 0, height: '100%' }} />
          <Box
            component="img"
            src={urlImagemProduto(atual, OPCOES_MINIATURA)}
            alt=""
            aria-hidden
            sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', filter: 'blur(12px)', transform: 'scale(1.08)' }}
          />
        </>
      )}
      <CardMedia
        component="img"
        image={urlAtual}
        alt={alt}
        onLoad={() => setCarregadas((atuais) => new Set(atuais).add(urlAtual))}
        sx={[
          { position: 'relative', display: 'block', transition: 'opacity 300ms ease', opacity: carregada ? 1 : 0 },
          // Sem as dimensões da foto a caixa teria altura zero e o skeleton não apareceria
          !carregada && { aspectRatio: '1 / 1' },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
      />
    </Box>
  );

  if (!varias) return imagem;

  // As setas são só para quem tem mouse (desktop). Em telas de toque — celular e
  // tablet — elas atrapalham a imagem: ali a troca é pelo arrasto, e as bolinhas
  // já indicam que há mais de uma imagem.
  const botaoSx = {
    display: 'none',
    '@media (hover: hover) and (pointer: fine)': { display: 'inline-flex' },
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    bgcolor: (t: Theme) => alpha(t.palette.background.paper, 0.85),
    color: 'text.primary',
    boxShadow: 1,
    '&:hover': { bgcolor: (t: Theme) => alpha(t.palette.background.paper, 0.95) },
  } as const;

  return (
    <Box
      sx={{ position: 'relative', width: '100%' }}
      onTouchStart={(e) => {
        inicioToque.current = e.touches[0]?.clientX ?? null;
      }}
      onTouchEnd={(e) => {
        const inicio = inicioToque.current;
        inicioToque.current = null;
        if (inicio == null) return;
        const delta = (e.changedTouches[0]?.clientX ?? inicio) - inicio;
        if (Math.abs(delta) < LIMIAR_SWIPE) return;
        irPara(indice + (delta < 0 ? 1 : -1));
      }}
    >
      {imagem}

      {/* Pré-carrega as outras para a troca não piscar. */}
      <Box sx={{ display: 'none' }} aria-hidden>
        {imagens.map((img, i) =>
          i === indice ? null : <img key={img.id} src={urlImagemProduto(img, opcoes)} alt="" />,
        )}
      </Box>

      <IconButton
        size="small"
        aria-label="Imagem anterior"
        onClick={() => irPara(indice - 1)}
        sx={{ ...botaoSx, left: 8 }}
      >
        <ChevronLeftIcon />
      </IconButton>
      <IconButton
        size="small"
        aria-label="Próxima imagem"
        onClick={() => irPara(indice + 1)}
        sx={{ ...botaoSx, right: 8 }}
      >
        <ChevronRightIcon />
      </IconButton>

      <Box
        sx={{
          position: 'absolute',
          bottom: recuoIndicadores,
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'center',
          gap: 0.75,
        }}
      >
        {imagens.map((img, i) => (
          <Box
            key={img.id}
            component="button"
            type="button"
            aria-label={`Imagem ${i + 1} de ${imagens.length}`}
            aria-current={i === indice}
            onClick={() => setIndice(i)}
            sx={{
              p: 0,
              border: 'none',
              cursor: 'pointer',
              width: i === indice ? 18 : 7,
              height: 7,
              borderRadius: 4,
              transition: 'width 240ms, background-color 240ms',
              bgcolor: (t) =>
                i === indice ? t.palette.primary.main : alpha(t.palette.common.black, 0.28),
              boxShadow: (t) => `0 0 0 1px ${alpha(t.palette.common.white, 0.6)}`,
            }}
          />
        ))}
      </Box>
    </Box>
  );
}
